import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { comprehensiveCreativeAnalysis } from '../services/comprehensiveCreativeAnalysis';
import { useAuth } from '../contexts/AuthContext';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import ComparisonChart from '../components/ComparisonChart';

const ComprehensiveCreativeReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccountId, getAccountName } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [llmLoading, setLlmLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [adNameFilter, setAdNameFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState(7);
  const [clusteredAdNames, setClusteredAdNames] = useState([]);
  const [llmInsights, setLlmInsights] = useState({});

  // Date range options
  const dateRanges = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 365 days', days: 365 }
  ];

  // Get SQL date range condition (matches working queries)
  const getSQLDateRange = () => {
    return `adp.date >= CURRENT_DATE - INTERVAL '${selectedDateRange} days'`;
  };

  // Generate comprehensive report
  const generateReport = async () => {
    // Prevent multiple simultaneous requests
    if (loading) {
      console.log('🚫 Report generation already in progress, skipping...');
      return;
    }

    // Clear previous data when starting new analysis
    setReportData({});
    setLlmInsights({});

    const accountId = getAccountId();
    console.log('🔍 Generating report for account:', accountId);
    
    if (!accountId) {
      toast.error('Please select an account first');
      return;
    }

    setLoading(true);
    const dateRange = getSQLDateRange();
    console.log('📅 Using date range:', dateRange);
    console.log('📅 selectedDateRange value:', selectedDateRange);
    console.log('📅 Generated SQL condition:', dateRange);
    
    // Declare variables at function scope so they're available throughout
    let copyContentClustering = null;
    const adNameFilterValue = adNameFilter.trim();

    try {
      // Generate all analysis sections in parallel
      console.log('🚀 Starting report generation...');
      
      // First get all the SQL-based analysis (faster)
      console.log('🚀 Starting SQL analysis...');
      let topPerformers, copyAnalysis, adFormatComparison, adTypeAnalysis;
      
      try {
        console.log('🔍 Calling individual functions to debug...');
        
        console.log('1️⃣ Calling getTopPerformersAnalysis for ROAS...');
        const topPerformersByROAS = await comprehensiveCreativeAnalysis.getTopPerformersAnalysis(accountId, dateRange, adNameFilter, 'roas');
        console.log('✅ getTopPerformersAnalysis (ROAS) completed');
        
        console.log('1️⃣ Calling getTopPerformersAnalysis for Spend...');
        const topPerformersBySpend = await comprehensiveCreativeAnalysis.getTopPerformersAnalysis(accountId, dateRange, adNameFilter, 'spend');
        console.log('✅ getTopPerformersAnalysis (Spend) completed');
        
        // Combine both datasets for the report
        topPerformers = {
          byROAS: topPerformersByROAS || [],
          bySpend: topPerformersBySpend || []
        };
        
        console.log('📊 Top Performers data received:', {
          roasCount: topPerformersByROAS?.length || 0,
          spendCount: topPerformersBySpend?.length || 0,
          roasTotalSpend: topPerformersByROAS?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
          spendTotalSpend: topPerformersBySpend?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
          sampleROASData: topPerformersByROAS?.slice(0, 3)?.map(ad => ({
            name: ad.ad_name,
            spend: ad.total_spend,
            roas: ad.roas
          })),
          sampleSpendData: topPerformersBySpend?.slice(0, 3)?.map(ad => ({
            name: ad.ad_name,
            spend: ad.total_spend,
            roas: ad.roas
          })),
          roasDataType: typeof topPerformersByROAS,
          spendDataType: typeof topPerformersBySpend,
          roasIsArray: Array.isArray(topPerformersByROAS),
          spendIsArray: Array.isArray(topPerformersBySpend)
        });
        
        // Validate data structure
        if (!Array.isArray(topPerformersByROAS)) {
          console.error('❌ topPerformersByROAS is not an array:', topPerformersByROAS);
        }
        if (!Array.isArray(topPerformersBySpend)) {
          console.error('❌ topPerformersBySpend is not an array:', topPerformersBySpend);
        }
        
        // Additional debugging for 30-day issue
        if (selectedDateRange === 30) {
          console.log('🔍 30-Day Debug - Raw Data:', {
            roasRaw: topPerformersByROAS,
            spendRaw: topPerformersBySpend,
            roasType: typeof topPerformersByROAS,
            spendType: typeof topPerformersBySpend,
            roasIsArray: Array.isArray(topPerformersByROAS),
            spendIsArray: Array.isArray(topPerformersBySpend)
          });
        }
        
        console.log('2️⃣ Calling getCopyAnalysis...');
        copyAnalysis = await comprehensiveCreativeAnalysis.getCopyAnalysis(accountId, dateRange, adNameFilter);
        console.log('✅ getCopyAnalysis completed');
        
        console.log('3️⃣ Calling getAdFormatComparison...');
        adFormatComparison = await comprehensiveCreativeAnalysis.getAdFormatComparison(accountId, dateRange, adNameFilter);
        console.log('✅ getAdFormatComparison completed');
        
        console.log('4️⃣ Calling getAdTypeAnalysis...');
        adTypeAnalysis = await comprehensiveCreativeAnalysis.getAdTypeAnalysis(accountId, dateRange, adNameFilter);
        console.log('✅ getAdTypeAnalysis completed');
        
        console.log('✅ All SQL analysis completed successfully');
      } catch (error) {
        console.error('💥 Error in SQL analysis:', error);
        // Set defaults if any function fails
        topPerformers = topPerformers || [];
        copyAnalysis = copyAnalysis || [];
        adFormatComparison = adFormatComparison || [];
        adTypeAnalysis = adTypeAnalysis || [];
      }

      // Debug: Check what we got from SQL analysis
      console.log('🔍 SQL Analysis Variables Check:', {
        topPerformers: topPerformers,
        copyAnalysis: copyAnalysis,
        adFormatComparison: adFormatComparison,
        adTypeAnalysis: adTypeAnalysis
      });

      // Then get LLM-based analysis (slower, but we need to wait for it)
      console.log('🤖 Starting LLM analysis...');
      setLlmLoading(true);
      try {
        copyContentClustering = await comprehensiveCreativeAnalysis.getCopyContentClustering(accountId, dateRange, adNameFilter);
        console.log('✅ LLM analysis completed');
        
        // Update report data with LLM results
        console.log('🔄 Before state update - Copy content clustering results received:', {
          copyContentClustering: {
            hasData: !!copyContentClustering,
            success: copyContentClustering?.success,
            error: copyContentClustering?.error,
            clusters: copyContentClustering?.clusters?.length || 0,
            type: typeof copyContentClustering,
            fullData: copyContentClustering
          }
        });
        
        // Check for LLM failures
        if (copyContentClustering?.success === false) {
          console.error('❌ Copy Content Clustering LLM failed:', copyContentClustering.error);
        }
        
        // Log LLM analysis results
        console.log('🤖 Copy Content Clustering Results:', {
          copyContentClustering: {
            hasData: !!copyContentClustering,
            clusters: copyContentClustering?.clusters?.length || 0,
            insights: copyContentClustering?.insights?.length || 0,
            recommendations: copyContentClustering?.recommendations?.length || 0,
            rawData: copyContentClustering?.rawCopyData?.length || 0,
            rawResponse: copyContentClustering?.rawResponse || 'No raw response',
            fullData: copyContentClustering
          }
        });
        
        // Debug: Check if data is actually being set
        setTimeout(() => {
          console.log('🔄 After timeout - checking if state was updated:', {
            currentReportData: reportData,
            copyContentClusteringExists: !!reportData.copyContentClustering,
            clustersLength: reportData.copyContentClustering?.clusters?.length || 0
          });
        }, 100);
      } finally {
        setLlmLoading(false);
      }
      
      console.log('📊 SQL Analysis Results:', {
        topPerformers: topPerformers?.length || 0,
        copyAnalysis: copyAnalysis?.length || 0,
        adFormatComparison: adFormatComparison?.length || 0,
        adTypeAnalysis: adTypeAnalysis?.length || 0
      });

      // Set final report data with BOTH SQL and LLM results
      const finalReportData = {
        topPerformers: {
          byROAS: Array.isArray(topPerformers?.byROAS) ? topPerformers.byROAS : [],
          bySpend: Array.isArray(topPerformers?.bySpend) ? topPerformers.bySpend : []
        },
        copyAnalysis,
        adFormatComparison,
        adTypeAnalysis,
        copyContentClustering
      };
      
      // Additional validation
      console.log('🔍 Final report data validation:', {
        topPerformersROAS: finalReportData.topPerformers.byROAS.length,
        topPerformersSpend: finalReportData.topPerformers.bySpend.length,
        topPerformersROASIsArray: Array.isArray(finalReportData.topPerformers.byROAS),
        topPerformersSpendIsArray: Array.isArray(finalReportData.topPerformers.bySpend)
      });
      
      console.log('📊 Final report data being set:', {
        topPerformersROASCount: topPerformers?.byROAS?.length || 0,
        topPerformersSpendCount: topPerformers?.bySpend?.length || 0,
        topPerformersROASTotalSpend: topPerformers?.byROAS?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
        topPerformersSpendTotalSpend: topPerformers?.bySpend?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
        dateRange: dateRange,
        selectedDateRange: selectedDateRange
      });
      
      setReportData(finalReportData);

      toast.success('Comprehensive report generated successfully!');
    } catch (error) {
      console.error('💥 Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get clusters data from either location
  const getClustersData = () => {
    if (reportData.copyContentClustering?.clusters && reportData.copyContentClustering.clusters.length > 0) {
      return reportData.copyContentClustering.clusters;
    }
    if (reportData.copyContentClustering?.fullData?.clusters && reportData.copyContentClustering.fullData.clusters.length > 0) {
      return reportData.copyContentClustering.fullData.clusters;
    }
    return [];
  };

  // Helper function to get insights data from either location
  const getInsightsData = () => {
    if (reportData.copyContentClustering?.insights && reportData.copyContentClustering.insights.length > 0) {
      return reportData.copyContentClustering.insights;
    }
    if (reportData.copyContentClustering?.fullData?.insights && reportData.copyContentClustering.fullData.insights.length > 0) {
      return reportData.copyContentClustering.fullData.insights;
    }
    return [];
  };

  // Helper function to get recommendations data from either location
  const getRecommendationsData = () => {
    if (reportData.copyContentClustering?.recommendations && reportData.copyContentClustering.recommendations.length > 0) {
      return reportData.copyContentClustering.recommendations;
    }
    if (reportData.copyContentClustering?.fullData?.recommendations && reportData.copyContentClustering.fullData.recommendations.length > 0) {
      return reportData.copyContentClustering.fullData.recommendations;
    }
    return [];
  };

  // Auto-generate report on mount and when date range changes
  useEffect(() => {
    generateReport();
  }, [selectedDateRange]); // Re-run when date range changes

  // Handle share report functionality
  const handleShareReport = async () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      setShareStatus('error');
      return;
    }

    setShareStatus('loading');
    
    try {
      // Get account name and date range for URL
      const accountName = getAccountName() || 'unknown-account';
      
      // Clean account name for URL (remove special characters, replace spaces with hyphens)
      const cleanAccountName = accountName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      const dateRange = selectedDateRange === 7 ? '7days' : 
                       selectedDateRange === 14 ? '14days' : 
                       selectedDateRange === 30 ? '30days' : 
                       selectedDateRange === 90 ? '90days' : 
                       selectedDateRange === 365 ? '365days' : '7days';
      const timestamp = Date.now();
      
      // Prepare the data to save
      const shareData = {
        accountId: getAccountId(),
        accountName: cleanAccountName,
        dateRange: dateRange,
        timestamp: timestamp,
        reportData: {
          topPerformers: reportData.topPerformers || [],
          copyContentClustering: {
            ...reportData.copyContentClustering,
            // Ensure insights and recommendations are included
            insights: getInsightsData(),
            recommendations: getRecommendationsData()
          },
          adTypeAnalysis: reportData.adTypeAnalysis || []
        },
        createdAt: new Date().toISOString(),
        status: 'completed'
      };

      // Save to database
      const response = await fetch('/api/reports/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shareData)
      });

      if (response.ok) {
        const result = await response.json();
        const shareUrl = `${window.location.origin}/shared-comprehensive-report/${cleanAccountName}/${dateRange}/${timestamp}`;
        setShareUrl(shareUrl);
        setShareStatus('ready');
        
        // Copy to clipboard
        navigator.clipboard.writeText(shareUrl);
        toast.success('Report shared! URL copied to clipboard.');
      } else {
        throw new Error('Failed to save report');
      }
    } catch (error) {
      console.error('Error sharing report:', error);
      setShareStatus('error');
      toast.error('Failed to share report. Please try again.');
    }
  };

  // Debug: Monitor reportData changes
  useEffect(() => {
    console.log('🔄 reportData changed:', {
      hasCopyContentClustering: !!reportData.copyContentClustering,
      clustersLength: reportData.copyContentClustering?.clusters?.length || 0,
      fullData: reportData.copyContentClustering,
      topPerformersROASCount: reportData.topPerformers?.byROAS?.length || 0,
      topPerformersSpendCount: reportData.topPerformers?.bySpend?.length || 0,
      topPerformersROASTotalSpend: reportData.topPerformers?.byROAS?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
      topPerformersSpendTotalSpend: reportData.topPerformers?.bySpend?.reduce((sum, ad) => sum + (parseFloat(ad.total_spend) || 0), 0) || 0,
      selectedDateRange: selectedDateRange
    });
  }, [reportData, selectedDateRange]);

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'topPerformers', label: 'Top Performers', icon: '🏆' },
    { id: 'copyThemes', label: 'Copy Themes', icon: '✍️' },
    { id: 'adTypeAnalysis', label: 'Ad Type Analysis', icon: '📱' }
  ];

  // Render overview tab
  const renderOverview = () => (
    <div className="space-y-6">


      {/* Ad Name Filter & LLM Clustering */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🎯 Ad Name Intelligence</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Ad Name
            </label>
            <input
              type="text"
              value={adNameFilter}
              onChange={(e) => setAdNameFilter(e.target.value)}
              placeholder="Enter ad name or keyword..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {/* Debug info */}
            <div className="mt-1 text-xs text-gray-500">
              Current filter: "{adNameFilter}" (length: {adNameFilter.length})
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {dateRanges.map(range => (
                <option key={range.days} value={range.days}>{range.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={generateReport}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* LLM Insights Summary */}
      {llmInsights.insights && llmInsights.insights.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">🤖 AI-Powered Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Key Patterns</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {llmInsights.insights.slice(0, 3).map((insight, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Recommendations</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {llmInsights.recommendations && llmInsights.recommendations.slice(0, 3).map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">→</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Ad Type Performance Summary */}
      {reportData.adTypeAnalysis && reportData.adTypeAnalysis.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📱 Ad Type Performance Overview</h3>
          
          {/* Debug Info */}
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
            <div className="font-medium text-yellow-800 mb-2">🐛 Debug: Raw Ad Type Data</div>
            <div className="text-yellow-700">
              <div>Total ad types: {reportData.adTypeAnalysis.length}</div>
              <div>Sample data: {JSON.stringify(reportData.adTypeAnalysis.slice(0, 2), null, 2)}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Top Performing Ad Type */}
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <h4 className="text-sm font-medium text-green-700 mb-2">🏆 Best Performing</h4>
              {(() => {
                const topType = reportData.adTypeAnalysis
                  .filter(type => parseFloat(type.roas || 0) > 0)
                  .sort((a, b) => parseFloat(b.roas || 0) - parseFloat(a.roas || 0))[0];
                return topType ? (
                  <>
                    <div className="text-xl font-bold text-green-800">{topType.ad_type}</div>
                    <div className="text-sm text-green-600">{topType.roas}x ROAS</div>
                    <div className="text-xs text-green-500">{topType.number_of_ads} ads</div>
                  </>
                ) : (
                  <div className="text-sm text-green-600">No data</div>
                );
              })()}
            </div>

            {/* Most Spent Ad Type */}
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700 mb-2">💰 Highest Spend</h4>
              {(() => {
                const highestSpend = reportData.adTypeAnalysis
                  .sort((a, b) => parseFloat(b.total_spend || 0) - parseFloat(a.total_spend || 0))[0];
                return highestSpend ? (
                  <>
                    <div className="text-xl font-bold text-blue-800">{highestSpend.ad_type}</div>
                    <div className="text-sm text-blue-600">${parseFloat(highestSpend.total_spend || 0).toLocaleString()}</div>
                    <div className="text-xs text-blue-500">{highestSpend.number_of_ads} ads</div>
                  </>
                ) : (
                  <div className="text-sm text-blue-600">No data</div>
                );
              })()}
            </div>

            {/* Most Ads Ad Type */}
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <h4 className="text-sm font-medium text-purple-700 mb-2">📊 Most Ads</h4>
              {(() => {
                const mostAds = reportData.adTypeAnalysis
                  .sort((a, b) => (b.number_of_ads || 0) - (a.number_of_ads || 0))[0];
                return mostAds ? (
                  <>
                    <div className="text-xl font-bold text-purple-800">{mostAds.ad_type}</div>
                    <div className="text-sm text-purple-600">{mostAds.number_of_ads} ads</div>
                    <div className="text-xs text-purple-500">{mostAds.roas}x ROAS</div>
                  </>
                ) : (
                  <div className="text-sm text-purple-600">No data</div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(reportData).map(([key, data]) => (
          <div key={key} className="bg-white rounded-lg shadow p-4">
            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </h4>
            <p className="text-2xl font-bold text-gray-900">
              {key === 'topPerformers' && data && typeof data === 'object' && data.byROAS ? 
                `${data.byROAS.length || 0} ROAS, ${data.bySpend.length || 0} Spend` :
                Array.isArray(data) ? data.length : 
                (data && typeof data === 'object' ? Object.keys(data).length : 0)
              }
            </p>
            <p className="text-sm text-gray-500">
              {key === 'topPerformers' && data && typeof data === 'object' && data.byROAS ? 'ads' : 'records'}
            </p>
          </div>
        ))}
      </div>

      {/* Debug Info for Top Performers */}
      {reportData.topPerformers && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">🐛 Debug: Top Performers Data Structure</h4>
          <div className="text-xs text-yellow-700 space-y-1">
            <div><strong>Type:</strong> {typeof reportData.topPerformers}</div>
            <div><strong>Keys:</strong> {reportData.topPerformers ? Object.keys(reportData.topPerformers).join(', ') : 'none'}</div>
            <div><strong>byROAS:</strong> {reportData.topPerformers.byROAS ? `${reportData.topPerformers.byROAS.length} items` : 'undefined'}</div>
            <div><strong>bySpend:</strong> {reportData.topPerformers.bySpend ? `${reportData.topPerformers.bySpend.length} items` : 'undefined'}</div>
            <div><strong>Sample ROAS data:</strong> {reportData.topPerformers.byROAS && reportData.topPerformers.byROAS.length > 0 ? 
              `${reportData.topPerformers.byROAS[0]?.ad_name || 'no name'} (${reportData.topPerformers.byROAS[0]?.roas || 'no roas'}x)` : 'none'}</div>
            <div><strong>Sample Spend data:</strong> {reportData.topPerformers.bySpend && reportData.topPerformers.bySpend.length > 0 ? 
              `${reportData.topPerformers.bySpend[0]?.ad_name || 'no name'} ($${reportData.topPerformers.bySpend[0]?.total_spend || 'no spend'})` : 'none'}</div>
          </div>
        </div>
      )}
    </div>
  );

  // State for modal preview
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  
  // State for top performers sorting
  const [topPerformersSort, setTopPerformersSort] = useState('roas'); // 'roas' or 'spend'
  
  // State for share functionality
  const [shareStatus, setShareStatus] = useState('idle'); // 'idle', 'loading', 'ready', 'error'
  const [shareUrl, setShareUrl] = useState('');

  // Handle ESC key and body scroll for modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && previewModal.isOpen) {
        setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
      }
    };

    if (previewModal.isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [previewModal.isOpen]);

  // Thumbnail component for displaying ad assets
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-16 h-16") => {
    if (!assetUrl) {
      return (
        <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <span className="text-xs text-gray-500 text-center">No Asset</span>
        </div>
      );
    }

    if (mediaFormat === 'Video') {
      return (
        <div 
          className={`${className} bg-gray-200 rounded-lg cursor-pointer hover:scale-105 transition-transform relative overflow-hidden`}
          onClick={() => setPreviewModal({ isOpen: true, assetUrl, mediaFormat, adName })}
          title={`Click to preview ${adName}`}
        >
          {/* Video Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-8 h-8 bg-black bg-opacity-70 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          {/* Video Thumbnail with Fallback */}
          <video 
            className="w-full h-full object-cover"
            muted
            preload="metadata"
            onError={(e) => {
              // Hide video and show fallback
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'flex';
            }}
          >
            <source src={assetUrl} type="video/mp4" />
          </video>
          
          {/* Fallback for video loading errors */}
          <div className="hidden absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
            <div className="text-center text-white">
              <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              <div className="text-xs font-medium">Video</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <img 
        src={assetUrl} 
        alt={adName}
        className={`${className} object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform`}
        onClick={() => setPreviewModal({ isOpen: true, assetUrl, mediaFormat, adName })}
        title={`Click to preview ${adName}`}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  };

  // Modal Preview Component
  const renderPreviewModal = () => {
    if (!previewModal.isOpen) return null;

    const closeModal = () => {
      setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
    };

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        onClick={closeModal}
      >
        <div 
          className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 truncate pr-4" title={previewModal.adName}>
              {previewModal.adName}
            </h3>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
              aria-label="Close preview"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {previewModal.mediaFormat === 'Video' ? (
              <video 
                className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                controls
                autoPlay
                preload="metadata"
                onError={(e) => {
                  console.error('Video preview error:', e);
                }}
              >
                <source src={previewModal.assetUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={previewModal.assetUrl} 
                alt={previewModal.adName}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  console.error('Image preview error:', e);
                }}
              />
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={closeModal}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
                <a
                  href={previewModal.assetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                >
                  Open in New Tab →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render top performers tab
  const renderTopPerformers = () => {
    // Debug: Log the data structure being processed
    console.log('🔍 renderTopPerformers Debug:', {
      hasTopPerformers: !!reportData.topPerformers,
      topPerformersType: typeof reportData.topPerformers,
      topPerformersKeys: reportData.topPerformers ? Object.keys(reportData.topPerformers) : [],
      byROAS: reportData.topPerformers?.byROAS,
      bySpend: reportData.topPerformers?.bySpend,
      byROASLength: reportData.topPerformers?.byROAS?.length || 0,
      bySpendLength: reportData.topPerformers?.bySpend?.length || 0,
      topPerformersSort: topPerformersSort,
      selectedDateRange: selectedDateRange
    });
    
    // Get the appropriate dataset based on sort criteria
    let topPerformersData = [];
    
    if (reportData.topPerformers?.byROAS && reportData.topPerformers?.bySpend) {
      if (topPerformersSort === 'roas') {
        topPerformersData = Array.isArray(reportData.topPerformers.byROAS) ? reportData.topPerformers.byROAS : [];
      } else {
        topPerformersData = Array.isArray(reportData.topPerformers.bySpend) ? reportData.topPerformers.bySpend : [];
      }
    } else if (reportData.topPerformers) {
      // Fallback for legacy data structure
      topPerformersData = Array.isArray(reportData.topPerformers) ? reportData.topPerformers : [];
    }
    
    // Additional safety check
    if (!Array.isArray(topPerformersData)) {
      console.error('❌ topPerformersData is not an array:', topPerformersData);
      topPerformersData = [];
    }
    
    console.log('🔍 Selected dataset:', {
      topPerformersData,
      topPerformersDataLength: topPerformersData?.length || 0,
      topPerformersDataType: typeof topPerformersData,
      topPerformersDataIsArray: Array.isArray(topPerformersData)
    });
    
    // Filter out ads without assets and validate data structure
    const sortedTopPerformers = topPerformersData
      .filter(ad => ad && typeof ad === 'object' && ad.asset_url && ad.asset_url.trim() !== '') // Only show ads with assets
      .slice(0, 10); // Take top 10
      
    console.log('🔍 After filtering:', {
      sortedTopPerformersLength: sortedTopPerformers?.length || 0,
      sampleFilteredData: sortedTopPerformers?.slice(0, 2)
    });

    return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">🏆 Top Performing Ads</h3>
            
            {/* Sort Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setTopPerformersSort('roas')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    topPerformersSort === 'roas'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Top ROAS
                </button>
                <button
                  onClick={() => setTopPerformersSort('spend')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    topPerformersSort === 'spend'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Top Spend
                </button>
              </div>
            </div>
          </div>

        {reportData.topPerformers && ((reportData.topPerformers.byROAS && reportData.topPerformers.byROAS.length > 0) || (reportData.topPerformers.bySpend && reportData.topPerformers.bySpend.length > 0)) ? (
            <div className="space-y-4">
              {/* Sort Indicator */}
              <div className="text-sm text-gray-500 mb-2">
                Showing top 10 ads by {topPerformersSort === 'roas' ? 'ROAS (Return on Ad Spend)' : 'Spend (Total Investment)'}
              </div>
              
              {sortedTopPerformers.length > 0 ? (
                sortedTopPerformers.map((ad, index) => {
                  // Validate ad object
                  if (!ad || typeof ad !== 'object') {
                    console.error('❌ Invalid ad object at index', index, ':', ad);
                    return null;
                  }
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start space-x-4">
                        {/* Rank Badge */}
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            index === 2 ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {index + 1}
                          </div>
                        </div>
                        
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          {renderThumbnail(ad.asset_url || '', ad.media_format || 'Unknown', ad.ad_name || 'Unknown Ad', "w-20 h-20")}
                        </div>
                        
                        {/* Ad Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="text-lg font-medium text-gray-900">{ad.ad_name || 'Unknown Ad'}</h4>
                              {ad.ad_type && ad.ad_type !== 'Unknown' && ad.ad_type !== 'N/A' ? (
                                <p className="text-sm text-gray-500">{ad.ad_type}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">Ad type not specified</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                ad.media_format === 'Video' ? 'bg-blue-100 text-blue-800' :
                                ad.media_format === 'Image' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {ad.media_format || 'Unknown'}
                              </span>
                              {/* Highlight the sort criteria */}
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                topPerformersSort === 'roas' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {topPerformersSort === 'roas' ? `${ad.roas || 0}x ROAS` : `$${parseFloat(ad.total_spend || 0).toLocaleString()}`}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">ROAS:</span>
                              <span className={`ml-2 font-medium ${
                                parseFloat(ad.roas || 0) >= 2.0 ? 'text-green-600' :
                                parseFloat(ad.roas || 0) >= 1.0 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {ad.roas || 0}x
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">CTR:</span>
                              <span className="ml-2 font-medium">{ad.ctr || 0}%</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Spend:</span>
                              <span className="ml-2 font-medium">${parseFloat(ad.total_spend || 0).toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Features:</span>
                              <span className="ml-2 font-medium text-gray-700">
                                {[ad.product_position, ad.setting, ad.lighting]
                                  .filter(feature => 
                                    feature && 
                                    feature !== 'Not Applicable' && 
                                    feature !== 'N/A' && 
                                    feature !== 'n/a' && 
                                    !feature.toLowerCase().includes('not applicable') &&
                                    !feature.toLowerCase().includes('n/a') &&
                                    !feature.toLowerCase().includes('unknown')
                                  )
                                  .join(', ') || 'Features not specified'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }).filter(Boolean) // Remove any null entries
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-6xl mb-4">🖼️</div>
                  <p className="text-gray-500 text-lg mb-2">No ads with assets found</p>
                  <p className="text-gray-400 text-sm">
                    All top performing ads in this date range don't have associated images or videos.
                  </p>
                </div>
              )}

              {/* Top Ads Comparison Chart */}
              {sortedTopPerformers.length > 0 && (
                <div className="mt-6">
                  <ComparisonChart
                    title={`Top Ads Comparison (by ${topPerformersSort === 'roas' ? 'ROAS' : 'Spend'})`}
                    data={sortedTopPerformers.map(ad => ({
                      name: (ad.ad_name || 'Unknown').substring(0, 30), // Truncate long names
                      spend: parseFloat(ad.total_spend || 0),
                      roas: parseFloat(ad.roas || 0)
                    }))}
                    xAxisKey="name"
                    metrics={[
                      { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                      { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                    ]}
                    height={400}
                    maxItems={10}
                  />
                </div>
              )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <p className="text-gray-500 text-lg mb-2">No top performers data available</p>
            <p className="text-gray-400 text-sm">
              {reportData.topPerformers ? 
                'Top performers data structure is empty or invalid. Please regenerate the report.' : 
                'Generate a report first to see top performing ads.'
              }
            </p>
            {reportData.topPerformers && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                <strong>Debug Info:</strong> Top performers data: {JSON.stringify(reportData.topPerformers, null, 2)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
  };

  // Render landing pages tab
  const renderLandingPages = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🎯 Landing Page Performance</h3>
        {reportData.landingPages && reportData.landingPages.length > 0 ? (
          <>
            {/* Landing Pages Comparison Chart */}
            <ComparisonChart
              title="Landing Pages Comparison"
              data={reportData.landingPages.map(page => ({
                name: `${page.landing_page_type} (${page.media_format})`,
                spend: parseFloat(page.total_spend || 0),
                roas: parseFloat(page.roas || 0),
                ctr: parseFloat(page.ctr || 0),
                conversions: parseFloat(page.total_conversions || 0)
              }))}
              xAxisKey="name"
              metrics={[
                { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
              ]}
              height={400}
              maxItems={15}
            />

            <div className="space-y-4">
              {reportData.landingPages.slice(0, 10).map((page, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{page.landing_page_type}</h4>
                    <span className="text-sm text-gray-500">{page.media_format}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">ROAS:</span>
                      <span className="ml-2 font-medium">{page.roas}x</span>
                    </div>
                    <div>
                      <span className="text-gray-500">CTR:</span>
                      <span className="ml-2 font-medium">{page.ctr}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Spend:</span>
                      <span className="ml-2 font-medium">${parseFloat(page.total_spend || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Conversions:</span>
                      <span className="ml-2 font-medium">{page.total_conversions}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-gray-500">No data available. Generate a report first.</p>
        )}
      </div>
    </div>
  );

  // Render copy themes tab
  const renderCopyThemes = () => {
    // Debug: Log what's actually in the state during render
    console.log('🎨 renderCopyThemes called with:', {
      hasCopyContentClustering: !!reportData.copyContentClustering,
      copyContentClustering: reportData.copyContentClustering,
      clusters: reportData.copyContentClustering?.clusters,
      clustersLength: reportData.copyContentClustering?.clusters?.length || 0,
      fullReportData: reportData
    });
    
    return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-2">✍️ Copy & Messaging Analysis</h3>
        <p className="text-sm text-gray-600 mb-4">Comprehensive creative analytics based on visual, audio and text in the ads</p>
        
        {/* LLM Analysis Loading State */}
        {llmLoading && (
          <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-blue-800">
                <div className="font-medium">🤖 AI Analysis in Progress...</div>
                <div className="text-sm">Analyzing copy content and generating theme clusters</div>
              </div>
            </div>
          </div>
        )}
        

        
        {/* LLM Copy Clustering Results */}
        {reportData.copyContentClustering && 
         ((reportData.copyContentClustering.clusters && reportData.copyContentClustering.clusters.length > 0) || 
          (reportData.copyContentClustering.fullData && reportData.copyContentClustering.fullData.clusters && reportData.copyContentClustering.fullData.clusters.length > 0)) && (
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-700 mb-3">🤖 AI-Powered Copy Theme Clusters</h4>
            
            {/* Data Coverage Summary */}
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                <strong>📊 Data Coverage:</strong> Analyzed {reportData.copyContentClustering?.totalSamples || 0} ads, 
                showing {getClustersData().reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0)} ads in {getClustersData().length} clusters
                {reportData.copyContentClustering?.totalSamples && getClustersData().reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) < reportData.copyContentClustering.totalSamples && 
                  ` (${Math.round((getClustersData().reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) / reportData.copyContentClustering.totalSamples) * 100)}% coverage)`
                }
              </div>
            </div>

            {/* Cluster Performance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-lg font-bold text-blue-600">
                  {getClustersData().length}
                </div>
                <div className="text-sm text-blue-700">Copy Themes</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-lg font-bold text-green-600">
                  {getClustersData().reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0)}
                </div>
                <div className="text-sm text-green-700">Total Ads</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-600">
                  {getClustersData().length > 0 ? 
                    (getClustersData().reduce((sum, cluster) => sum + (cluster.performance?.avg_roas || 0), 0) / getClustersData().length).toFixed(1) : '0'}x
                </div>
                <div className="text-sm text-purple-700">Avg ROAS</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-lg font-bold text-orange-600">
                  {getClustersData().length > 0 ? 
                    (getClustersData().reduce((sum, cluster) => sum + (cluster.performance?.avg_ctr || 0), 0) / getClustersData().length).toFixed(1) : '0'}%
                </div>
                <div className="text-sm text-orange-700">Avg CTR</div>
              </div>
            </div>

            {/* Copy Themes Comparison Chart */}
            {getClustersData().length > 0 && (
              <div className="mb-6">
                <ComparisonChart
                  title="Copy Themes Comparison"
                  data={getClustersData().map(cluster => ({
                    name: (cluster.theme_name || 'Unknown').substring(0, 30),
                    spend: parseFloat(cluster.performance?.total_spend || 0),
                    roas: parseFloat(cluster.performance?.avg_roas || 0),
                    ctr: parseFloat(cluster.performance?.avg_ctr || 0),
                    revenue: parseFloat(cluster.performance?.total_revenue || 0)
                  }))}
                  xAxisKey="name"
                  metrics={[
                    { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                    { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                  ]}
                  height={400}
                  maxItems={15}
                />
              </div>
            )}

            {/* Copy Theme Clusters */}
            <div className="space-y-4">
              {getClustersData().map((cluster, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-gray-900 text-lg">{cluster.theme_name}</h5>
                    </div>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {cluster.ads?.length || 0} ads
                    </span>
                  </div>
                  
                  {/* Cluster Performance Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">ROAS</div>
                      <div className="font-medium text-gray-900">{cluster.performance?.avg_roas || 'N/A'}x</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">CTR</div>
                      <div className="font-medium text-gray-900">{cluster.performance?.avg_ctr || 'N/A'}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">CVR</div>
                      <div className="font-medium text-gray-900">{cluster.performance?.avg_cvr || 'N/A'}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">Spend</div>
                      <div className="font-medium text-gray-900">${parseFloat(cluster.performance?.total_spend || 0).toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">Revenue</div>
                      <div className="font-medium text-gray-900">${parseFloat(cluster.performance?.total_revenue || 0).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Sample Ads in Cluster */}
                  {cluster.ads && cluster.ads.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500 mb-2">Sample Ads in this theme:</div>
                      <div className="flex flex-wrap gap-2">
                        {cluster.ads.slice(0, 5).map((adId, adIndex) => {
                          const adData = reportData.copyContentClustering.rawCopyData?.find(ad => ad.raw_ad_id === adId);
                          return (
                            <span key={adIndex} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700">
                              {adData?.ad_name || adId}
                            </span>
                          );
                        })}
                        {cluster.ads.length > 5 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                            +{cluster.ads.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Copy Analytics Grid */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">📊 Copy Analytics for this Theme</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Top Hooks */}
                      {cluster.top_hooks && cluster.top_hooks.length > 0 && cluster.top_hooks.some(hook => 
                        hook.performance && !hook.performance.includes('0x ROAS') && !hook.performance.includes('0% CTR') && !hook.performance.includes('0% CVR')
                      ) && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-blue-800 mb-3">🎯 Top Hooks</h4>
                          <div className="space-y-2">
                            {cluster.top_hooks.slice(0, 3).map((hook, idx) => (
                              <div key={idx} className="bg-white rounded border border-blue-200 p-3">
                                <p className="text-xs text-gray-800 font-medium mb-1">"{hook.hook}"</p>
                                <p className="text-xs text-blue-600">{hook.performance}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Top CTAs */}
                      {cluster.top_ctas && cluster.top_ctas.length > 0 && cluster.top_ctas.some(cta => 
                        cta.performance && !cta.performance.includes('0x ROAS') && !cta.performance.includes('0% CTR') && !cta.performance.includes('0% CVR')
                      ) && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-orange-800 mb-3">📢 Top CTAs</h4>
                          <div className="space-y-2">
                            {cluster.top_ctas.slice(0, 3).map((cta, idx) => (
                              <div key={idx} className="bg-white rounded border border-orange-200 p-3">
                                <p className="text-xs text-gray-800 font-medium mb-1">"{cta.cta}"</p>
                                <p className="text-xs text-orange-600">{cta.performance}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Top Offers */}
                      {cluster.top_offers && cluster.top_offers.length > 0 && cluster.top_offers.some(offer => 
                        offer.performance && !offer.performance.includes('0x ROAS') && !offer.performance.includes('0% CTR') && !offer.performance.includes('0% CVR')
                      ) && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-purple-800 mb-3">💰 Top Offers</h4>
                          <div className="space-y-2">
                            {cluster.top_offers.slice(0, 3).map((offer, idx) => (
                              <div key={idx} className="bg-white rounded border border-purple-200 p-3">
                                <p className="text-xs text-gray-800 font-medium mb-1">"{offer.offer}"</p>
                                <p className="text-xs text-purple-600">{offer.performance}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sales Messaging */}
                      {cluster.sales_messaging && cluster.sales_messaging.length > 0 && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-teal-800 mb-3">💡 Sales Messaging Patterns</h4>
                          <div className="space-y-2">
                            {cluster.sales_messaging.slice(0, 3).map((pattern, idx) => (
                              <div key={idx} className="bg-white rounded border border-teal-200 p-3">
                                <p className="text-xs text-gray-800">{pattern}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Copy Examples */}
                  {cluster.copy_examples && cluster.copy_examples.length > 0 && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <h5 className="text-sm font-medium text-green-800 mb-2">✍️ Copy Examples</h5>
                      <div className="space-y-3">
                        {cluster.copy_examples.map((example, idx) => {
                          // Find the corresponding ad data to get asset URL
                          const adData = reportData.copyContentClustering?.rawCopyData?.find(ad => ad.ad_name === example.ad_name);
                          return (
                          <div key={idx} className="p-3 bg-white rounded border border-green-200">
                              <div className="flex items-start space-x-3">
                                {/* Thumbnail */}
                                <div className="flex-shrink-0">
                                  {renderThumbnail(
                                    adData?.asset_url, 
                                    adData?.media_format, 
                                    example.ad_name, 
                                    "w-16 h-16"
                                  )}
                                </div>
                                
                                {/* Copy Content */}
                                <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                              <h6 className="text-sm font-medium text-green-700">{example.ad_name}</h6>
                              <div className="text-xs text-green-600">
                                ROAS: {example.performance?.roas && typeof example.performance.roas === 'number' 
                                  ? example.performance.roas.toFixed(2) 
                                  : example.performance?.roas || 'N/A'}x | 
                                CTR: {example.performance?.ctr && typeof example.performance.ctr === 'number' 
                                  ? example.performance.ctr.toFixed(2) 
                                  : example.performance?.ctr || 'N/A'}% | 
                                CVR: {example.performance?.cvr && typeof example.performance.cvr === 'number' 
                                  ? example.performance.cvr.toFixed(2) 
                                  : example.performance?.cvr || 'N/A'}%
                              </div>
                            </div>
                            
                            {example.headline && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Headline:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.headline}</p>
                              </div>
                            )}
                            
                            {example.primary_text && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Primary Text:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.primary_text}</p>
                              </div>
                            )}
                            
                            {example.description && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Description:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.description}</p>
                              </div>
                            )}
                            
                            {example.audio_text && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Audio Text:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.audio_text}</p>
                              </div>
                            )}
                            
                            {example.text_overlay && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Text Overlay:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.text_overlay}</p>
                              </div>
                            )}
                            
                            {example.visual_description && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-600">Visual Description:</span>
                                <p className="text-sm text-gray-800 mt-1">{example.visual_description}</p>
                              </div>
                            )}
                          </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* LLM Insights */}
            {getInsightsData().length > 0 && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h5 className="font-medium text-yellow-800 mb-2">💡 AI Insights</h5>
                <ul className="space-y-1 text-sm text-yellow-700">
                  {getInsightsData().map((insight, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* LLM Recommendations */}
            {reportData.copyContentClustering.recommendations && reportData.copyContentClustering.recommendations.length > 0 && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-medium text-green-800 mb-2">🚀 AI Recommendations</h5>
                <ul className="space-y-1 text-sm text-green-700">
                  {reportData.copyContentClustering.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-600 mr-2">→</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Copy Analysis by Feature */}
        {reportData.copyContentClustering && reportData.copyContentClustering.fullData && reportData.copyContentClustering.fullData.clusters && reportData.copyContentClustering.fullData.clusters.length > 0 ? (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-700 mb-3">Copy Feature Performance</h4>
            {reportData.copyContentClustering.fullData.clusters.slice(0, 15).map((copy, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-900">{copy.feature_name}</h4>
                  <span className="text-sm text-gray-500">{copy.media_format}</span>
                </div>
                <div className="text-sm text-gray-600 mb-2">{copy.feature_value}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">ROAS:</span>
                    <span className="ml-2 font-medium">{copy.roas}x</span>
                  </div>
                  <div>
                    <span className="text-gray-500">CTR:</span>
                    <span className="ml-2 font-medium">{copy.ctr}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Spend:</span>
                    <span className="ml-2 font-medium">${parseFloat(copy.total_spend || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Ads:</span>
                    <span className="ml-2 font-medium">{copy.number_of_ads}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Copy Clustering Insights and Examples */}
        {reportData.copyContentClustering && reportData.copyContentClustering.fullData && reportData.copyContentClustering.fullData.clusters && reportData.copyContentClustering.fullData.clusters.length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="text-md font-medium text-gray-700 mb-3">🤖 AI-Generated Copy Insights</h4>
            
            {/* Copy Clusters with Examples */}
            {reportData.copyContentClustering.fullData.clusters.map((cluster, index) => (
              <div key={index} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex justify-between items-start mb-3">
                  <h5 className="font-medium text-blue-800">{cluster.cluster_name || `Cluster ${index + 1}`}</h5>
                  <span className="text-sm text-blue-600">{cluster.ads_count || 0} ads</span>
                </div>
                
                {/* Copy Examples */}
                {cluster.copy_examples && cluster.copy_examples.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-sm font-medium text-blue-700 mb-2">📝 Copy Examples:</h6>
                    <div className="space-y-2">
                      {cluster.copy_examples.map((example, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-blue-100">
                          <div className="text-sm text-blue-800 mb-1">
                            <strong>Headline:</strong> {example.headline || 'N/A'}
                          </div>
                          <div className="text-sm text-blue-700 mb-1">
                            <strong>Primary Text:</strong> {example.primary_text || 'N/A'}
                          </div>
                          <div className="text-sm text-blue-600">
                            <strong>Description:</strong> {example.description || 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Performance Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-blue-600">Avg ROAS:</span>
                    <span className="ml-2 font-medium">{cluster.avg_roas || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-blue-600">Avg CTR:</span>
                    <span className="ml-2 font-medium">{cluster.avg_ctr || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-blue-600">Total Spend:</span>
                    <span className="ml-2 font-medium">${parseFloat(cluster.total_spend || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-blue-600">Ads Count:</span>
                    <span className="ml-2 font-medium">{cluster.ads_count || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Raw Copy Content Data (when LLM clustering not available) */}
        {reportData.copyContentClustering && reportData.copyContentClustering.fullData && reportData.copyContentClustering.fullData.rawCopyData && reportData.copyContentClustering.fullData.rawCopyData.length > 0 && 
         (!reportData.copyContentClustering.fullData.clusters || reportData.copyContentClustering.fullData.clusters.length === 0) && !llmLoading && (
          <div className="mt-6">
            <h4 className="text-md font-medium text-gray-700 mb-3">📝 Raw Copy Content Data</h4>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-3">
                <strong>Note:</strong> Copy content data is available but LLM clustering is still processing. 
                This shows the raw copy content (headlines, primary text, descriptions) that will be analyzed.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Copy Samples:</span>
                  <span className="ml-2 font-medium">{reportData.copyContentClustering.fullData.rawCopyData.length}</span>
                </div>
                <div>
                  <span className="text-gray-500">With Headlines:</span>
                  <span className="ml-2 font-medium">
                    {reportData.copyContentClustering.fullData.rawCopyData.filter(c => c.headline && c.headline.trim()).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">With Primary Text:</span>
                  <span className="ml-2 font-medium">
                    {reportData.copyContentClustering.fullData.rawCopyData.filter(c => c.primary_text && c.primary_text.trim()).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">With Descriptions:</span>
                  <span className="ml-2 font-medium">
                    {reportData.copyContentClustering.fullData.rawCopyData.filter(c => c.description && c.description.trim()).length}
                  </span>
                </div>
              </div>
              
              {/* Debug: Show LLM response when clustering fails */}
              {reportData.copyContentClustering.rawResponse && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h5 className="text-sm font-medium text-red-800 mb-2">🐛 Debug: LLM Response</h5>
                  <div className="text-xs text-red-700 bg-white p-2 rounded border overflow-auto max-h-32">
                    <pre>{reportData.copyContentClustering.rawResponse}</pre>
                  </div>
                  {reportData.copyContentClustering.parseError && (
                    <p className="text-xs text-red-600 mt-2">Parse Error: {reportData.copyContentClustering.parseError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };





  // Render ad format comparison tab
  const renderAdFormatComparison = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">📱 Ad Type Analysis</h3>
        {reportData.adFormatComparison && reportData.adFormatComparison.length > 0 ? (
          <div className="space-y-6">
            {/* Ad Type Performance Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media Format</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Ads</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CVR</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.adFormatComparison.map((format, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {format.ad_format}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format.media_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format.number_of_ads}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format.roas}x</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format.ctr}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format.cvr}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(format.total_spend || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Comparison Chart */}
            {reportData.adFormatComparison && reportData.adFormatComparison.length > 0 && (
              <ComparisonChart
                title="Ad Format Comparison"
                data={reportData.adFormatComparison.map(format => ({
                  name: `${format.ad_format} (${format.media_type})`,
                  spend: parseFloat(format.total_spend || 0),
                  roas: parseFloat(format.roas || 0),
                  ctr: parseFloat(format.ctr || 0)
                }))}
                xAxisKey="name"
                metrics={[
                  { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                  { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                ]}
                height={400}
                maxItems={15}
              />
            )}
          </div>
        ) : (
          <p className="text-gray-500">No data available. Generate a report first.</p>
        )}
      </div>
    </div>
  );

  // Render ad type analysis tab
  const renderAdTypeAnalysis = () => {
    // Filter out unknown or non-applicable ad types for cleaner UI
    const filteredAdTypes = (reportData.adTypeAnalysis || []).filter(type => 
      type.ad_type && 
      type.ad_type !== 'Unknown' && 
      type.ad_type !== 'N/A' && 
      type.ad_type !== 'n/a' && 
      !type.ad_type.toLowerCase().includes('unknown') &&
      !type.ad_type.toLowerCase().includes('n/a') &&
      !type.ad_type.toLowerCase().includes('not applicable')
    );

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📱 Ad Type Performance Analysis</h3>
          {filteredAdTypes && filteredAdTypes.length > 0 ? (
          <div className="space-y-6">
            {/* Performance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {filteredAdTypes.length}
                </div>
                <div className="text-sm text-blue-700">Ad Types</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  ${filteredAdTypes.reduce((sum, type) => sum + parseFloat(type.total_spend || 0), 0).toLocaleString()}
                </div>
                <div className="text-sm text-green-700">Total Spend</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {filteredAdTypes.reduce((sum, type) => sum + parseInt(type.number_of_ads || 0), 0)}
                </div>
                <div className="text-sm text-purple-700">Total Ads</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  ${filteredAdTypes.reduce((sum, type) => sum + parseFloat(type.total_revenue || 0), 0).toLocaleString()}
                </div>
                <div className="text-sm text-yellow-700">Total Revenue</div>
              </div>
            </div>

            {/* Ad Types Comparison Chart */}
            {filteredAdTypes.length > 0 && (
              <ComparisonChart
                title="Ad Types Comparison"
                data={filteredAdTypes.map(type => ({
                  name: type.ad_type || 'Unknown',
                  spend: parseFloat(type.total_spend || 0),
                  roas: parseFloat(type.roas || 0),
                  ctr: parseFloat(type.ctr || 0),
                  revenue: parseFloat(type.total_revenue || 0)
                }))}
                xAxisKey="name"
                metrics={[
                  { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                  { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                ]}
                height={400}
                maxItems={15}
              />
            )}

            {/* Top vs Bottom Performers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Performing Ad Types */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-lg font-medium text-green-800 mb-4">🏆 Top Performing Ad Types (Top 50%)</h4>
                <div className="space-y-3">
                  {(() => {
                    const validAdTypes = filteredAdTypes.filter(type => parseFloat(type.roas || 0) > 0);
                    const sortedAdTypes = validAdTypes.sort((a, b) => parseFloat(b.roas || 0) - parseFloat(a.roas || 0));
                    const midPoint = Math.ceil(sortedAdTypes.length / 2);
                    const topHalf = sortedAdTypes.slice(0, midPoint);
                    
                    return topHalf.map((type, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-white rounded border border-green-200">
                        <div>
                          <div className="font-medium text-green-700">{type.ad_type}</div>
                          <div className="text-sm text-green-600">{type.number_of_ads} ads</div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-lg font-bold text-green-800">{type.roas}x ROAS</span>
                            {type.benchmark && type.benchmark.avg_roas && (() => {
                              const roasDiff = ((parseFloat(type.roas) - parseFloat(type.benchmark.avg_roas)) / parseFloat(type.benchmark.avg_roas) * 100).toFixed(1);
                              const ctrDiff = ((parseFloat(type.ctr) - parseFloat(type.benchmark.avg_ctr || 0)) / parseFloat(type.benchmark.avg_ctr || 1) * 100).toFixed(1);
                              const roasSign = roasDiff >= 0 ? '+' : '';
                              const ctrSign = ctrDiff >= 0 ? '+' : '';
                              
                              return (
                                <div className="relative group">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs cursor-help hover:bg-blue-200 transition-colors">
                                    ℹ️
                                  </div>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                    <div className="font-medium mb-1">vs Platform Average:</div>
                                    <div className={roasDiff >= 0 ? 'text-green-300' : 'text-red-300'}>ROAS: {roasSign}{roasDiff}% (avg: {type.benchmark.avg_roas}x)</div>
                                    <div className={ctrDiff >= 0 ? 'text-green-300' : 'text-red-300'}>CTR: {ctrSign}{ctrDiff}% (avg: {type.benchmark.avg_ctr || 'N/A'}%)</div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="text-sm text-green-600">
                            {type.ctr}% CTR
                            {type.media_type && (
                              <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                {type.media_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Bottom Performing Ad Types - Bottom Half by ROAS */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-lg font-medium text-red-800 mb-4">⚠️ Underperforming Ad Types (Bottom 50%)</h4>
                <div className="space-y-3">
                  {(() => {
                    const validAdTypes = filteredAdTypes.filter(type => parseFloat(type.roas || 0) > 0);
                    const sortedAdTypes = validAdTypes.sort((a, b) => parseFloat(b.roas || 0) - parseFloat(a.roas || 0));
                    const midPoint = Math.ceil(sortedAdTypes.length / 2);
                    const bottomHalf = sortedAdTypes.slice(midPoint);
                    
                    return bottomHalf.map((type, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-white rounded border border-red-200">
                        <div>
                          <div className="font-medium text-red-700">{type.ad_type}</div>
                          <div className="text-sm text-red-600">{type.number_of_ads} ads</div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-lg font-bold text-red-800">{type.roas}x ROAS</span>
                            {type.benchmark && type.benchmark.avg_roas && (() => {
                              const roasDiff = ((parseFloat(type.roas) - parseFloat(type.benchmark.avg_roas)) / parseFloat(type.benchmark.avg_roas) * 100).toFixed(1);
                              const ctrDiff = ((parseFloat(type.ctr) - parseFloat(type.benchmark.avg_ctr || 0)) / parseFloat(type.benchmark.avg_ctr || 1) * 100).toFixed(1);
                              const roasSign = roasDiff >= 0 ? '+' : '';
                              const ctrSign = ctrDiff >= 0 ? '+' : '';
                              
                              return (
                                <div className="relative group">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs cursor-help hover:bg-blue-200 transition-colors">
                                    ℹ️
                                  </div>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                    <div className="font-medium mb-1">vs Platform Average:</div>
                                    <div className={roasDiff >= 0 ? 'text-green-300' : 'text-red-300'}>ROAS: {roasSign}{roasDiff}% (avg: {type.benchmark.avg_roas}x)</div>
                                    <div className={ctrDiff >= 0 ? 'text-green-300' : 'text-red-300'}>CTR: {ctrSign}{ctrDiff}% (avg: {type.benchmark.avg_ctr || 'N/A'}%)</div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="text-sm text-red-600">
                            {type.ctr}% CTR
                            {type.media_type && (
                              <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                {type.media_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Detailed Performance Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Ads</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CVR</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPM</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdTypes.map((type, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {type.ad_type}
                          {type.media_type && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              {type.media_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.number_of_ads}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <span>{type.roas}x</span>
                          {type.benchmark && type.benchmark.avg_roas && (() => {
                            const roasDiff = ((parseFloat(type.roas) - parseFloat(type.benchmark.avg_roas)) / parseFloat(type.benchmark.avg_roas) * 100).toFixed(1);
                            const ctrDiff = ((parseFloat(type.ctr) - parseFloat(type.benchmark.avg_ctr || 0)) / parseFloat(type.benchmark.avg_ctr || 1) * 100).toFixed(1);
                            const roasSign = roasDiff >= 0 ? '+' : '';
                            const ctrSign = ctrDiff >= 0 ? '+' : '';
                            
                            return (
                              <div className="relative group">
                                <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs cursor-help hover:bg-blue-200 transition-colors">
                                  ℹ️
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  <div className="font-medium mb-1">vs Platform Average:</div>
                                  <div className={roasDiff >= 0 ? 'text-green-300' : 'text-red-300'}>ROAS: {roasSign}{roasDiff}% (avg: {type.benchmark.avg_roas}x)</div>
                                  <div className={ctrDiff >= 0 ? 'text-green-300' : 'text-red-300'}>CTR: {ctrSign}{ctrDiff}% (avg: {type.benchmark.avg_ctr || 'N/A'}%)</div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.ctr}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type.cvr}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${type.cpm}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(type.total_spend || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(type.total_revenue || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Performance Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ROAS by Ad Type */}
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredAdTypes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ad_type" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="roas" fill="#10B981" name="ROAS" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* CTR by Ad Type */}
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredAdTypes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ad_type" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ctr" fill="#3B82F6" name="CTR %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">No ad type data available. Generate a report first.</p>
        )}
      </div>
    </div>
  );
};





  // Tab content renderer
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'topPerformers':
        return renderTopPerformers();
      case 'landingPages':
        return renderLandingPages();
      case 'copyThemes':
        return renderCopyThemes();
      case 'adTypeAnalysis':
        return renderAdTypeAnalysis();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Comprehensive Creative Analysis
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Share Button */}
              <button
                onClick={handleShareReport}
                disabled={shareStatus === 'loading' || !reportData || Object.keys(reportData).length === 0 || loading}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  shareStatus === 'idle' && reportData && Object.keys(reportData).length > 0 && !loading
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : shareStatus === 'loading'
                    ? 'bg-gray-500 text-white cursor-not-allowed'
                    : shareStatus === 'ready'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : shareStatus === 'error'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-400 text-white cursor-not-allowed'
                }`}
              >
                {shareStatus === 'idle' && reportData && Object.keys(reportData).length > 0 && !loading && '📤 Share Report'}
                {shareStatus === 'idle' && (!reportData || Object.keys(reportData).length === 0 || loading) && '⏳ Generate Report First'}
                {shareStatus === 'loading' && '⏳ Creating...'}
                {shareStatus === 'ready' && '✅ Ready to Share'}
                {shareStatus === 'error' && '❌ Share Failed'}
              </button>
              
              {shareStatus === 'ready' && shareUrl && (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="px-3 py-1 text-sm border border-gray-300 rounded bg-gray-50 text-gray-600 w-80"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Copy
                  </button>
                </div>
              )}
              
            <div className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          {renderTabContent()}
        </div>
      </div>
      
      {/* Asset Preview Modal */}
      {renderPreviewModal()}
    </div>
  );
};

export default ComprehensiveCreativeReport;
